import { isPastDbTimestamp, isSsoSignInErrorRef, normalizeIssuer } from "@repo/shared";
import {
  SSO_AUTHORIZATION_ERROR_CODES,
  SSO_TOKEN_ERROR_CODES,
  type SsoCallbackTestTarget,
  type SsoIssuerConfirmationRequiredResponse,
  type SsoProviderIdChange,
  type SsoSignInErrorResponse,
  type SsoTestConnectionResponse,
} from "@repo/shared/types";
import { AlertCircle, CheckCircle2, FlaskConical } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ApiError, apiErrorCode } from "@/lib/api/_client";
import {
  useConfirmSsoProviderIdChange,
  useDeleteSsoProvider,
  useSsoSignInError,
  useStartSsoCallbackTest,
  useTestSsoConnection,
  useUpdateSsoProvider,
} from "@/lib/hooks/use-sso";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { IssuerDiffersAlert } from "./issuer-differs-alert";
import { T } from "./sso-settings.i18n";

/** Set by the API when a test sign-in returns (sso-callback-test-service.ts). */
const CALLBACK_TEST_PARAM = "callbackTest";
const CALLBACK_TEST_TARGET_PARAM = "callbackTestTarget";
const CALLBACK_TEST_REASON_PARAM = "callbackTestReason";
/** QL-637 — the reference of the recorded failure, when the test failed. */
const CALLBACK_TEST_REF_PARAM = "callbackTestRef";

type SsoSettingsProps = {
  ssoEnabled: boolean;
  isConfigured: boolean;
  providerId?: string;
  callbackUrl?: string;
  oidcIssuer?: string;
  oidcClientId?: string;
  oidcName?: string;
  /** QL-618 — a client secret is stored; blank keeps it. */
  hasClientSecret?: boolean;
  /** QL-345 — an unconfirmed provider id change after a slug rename. */
  pendingChange?: SsoProviderIdChange | null;
};

export function SsoSettings({
  ssoEnabled: initialSsoEnabled,
  isConfigured,
  providerId,
  callbackUrl,
  pendingChange = null,
  oidcIssuer: initialOidcIssuer,
  oidcClientId: initialOidcClientId,
  oidcName: initialOidcName,
  hasClientSecret = false,
}: SsoSettingsProps) {
  const t = useT(T);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<SsoTestConnectionResponse | null>(null);
  // QL-619 — the identity provider reports another issuer than entered (from a check or a 409
  // on save); saving then needs the administrator to confirm exactly that discovered value
  const [issuerDiff, setIssuerDiff] = useState<{ entered: string; discovered: string } | null>(
    null,
  );
  const [issuerConfirmed, setIssuerConfirmed] = useState(false);

  const [ssoEnabled, setSsoEnabled] = useState(initialSsoEnabled);
  const [formData, setFormData] = useState({
    oidcIssuer: initialOidcIssuer || "",
    oidcClientId: initialOidcClientId || "",
    oidcClientSecret: "",
    oidcName: initialOidcName || "",
  });

  const updateMutation = useUpdateSsoProvider();
  const deleteMutation = useDeleteSsoProvider();
  const testMutation = useTestSsoConnection();
  const confirmMutation = useConfirmSsoProviderIdChange();
  const callbackTestMutation = useStartSsoCallbackTest();
  const { formatDate } = useRegionalFormat();
  const [removeSecretOpen, setRemoveSecretOpen] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const isPending =
    updateMutation.isPending ||
    deleteMutation.isPending ||
    testMutation.isPending ||
    confirmMutation.isPending ||
    callbackTestMutation.isPending;

  // The result of a test sign-in lands here as ?callbackTest=…; read once
  const [callbackTest] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get(CALLBACK_TEST_PARAM);
    return outcome
      ? {
          outcome,
          target: params.get(CALLBACK_TEST_TARGET_PARAM) as SsoCallbackTestTarget | null,
          reason: params.get(CALLBACK_TEST_REASON_PARAM),
          ref: params.get(CALLBACK_TEST_REF_PARAM),
        }
      : null;
  });
  useEffect(() => {
    if (!callbackTest) return;
    const params = new URLSearchParams(window.location.search);
    params.delete(CALLBACK_TEST_PARAM);
    params.delete(CALLBACK_TEST_TARGET_PARAM);
    params.delete(CALLBACK_TEST_REASON_PARAM);
    params.delete(CALLBACK_TEST_REF_PARAM);
    const query = params.toString();
    window.history.replaceState({}, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
  }, [callbackTest]);

  // QL-637 — the failed test-result alert's detail and the "Look up a failed
  // sign-in" box each need their own query: sharing one hook/key let a stale
  // test outcome hijack a later lookup for an unrelated reference (review SF3).
  const testResultQuery = useSsoSignInError(
    callbackTest?.outcome === "failed" ? (callbackTest.ref ?? null) : null,
  );

  // "Look up a failed sign-in" box: a reference a user reported.
  const [lookupInput, setLookupInput] = useState("");
  const [lookupRef, setLookupRef] = useState<string | null>(null);
  const [lookupInvalid, setLookupInvalid] = useState(false);
  const lookupQuery = useSsoSignInError(lookupRef);
  const handleLookup = () => {
    const trimmed = lookupInput.trim();
    if (!isSsoSignInErrorRef(trimmed)) {
      setLookupInvalid(true);
      setLookupRef(null);
      return;
    }
    setLookupInvalid(false);
    // Same reference again (e.g. after a failed lookup): the key doesn't change, so refetch.
    if (trimmed === lookupRef) {
      void lookupQuery.refetch();
      return;
    }
    setLookupRef(trimmed);
  };

  /** Plain text nodes only (QL-637): the identity provider's description is never markdown/HTML. */
  const renderSignInErrorDetail = (entry: SsoSignInErrorResponse | null | undefined) => {
    if (!entry) return null;
    return (
      <div className="mt-2 min-w-0 space-y-1 text-xs">
        <p className="min-w-0">
          <span className="font-medium">
            {entry.descriptionTrusted ? t.idpMessageLabel : t.idpMessageUnverifiedLabel}
          </span>{" "}
          {/* A long unbroken IdP token (URL, trace id, base64…) has no space to wrap at;
              `break-words` alone doesn't shrink the grid track's min-content size and the
              alert overflows (review F2) — `overflow-wrap: anywhere` does. */}
          <span className="break-words [overflow-wrap:anywhere]">
            {entry.description ?? t.idpMessageNone}
          </span>
        </p>
        {/* QL-637 — text from a sign-in URL anyone can produce is never presented as the provider's. */}
        {!entry.descriptionTrusted && entry.description && (
          <p className="text-muted-foreground">{t.idpMessageUnverifiedHint}</p>
        )}
        <p>
          {t.referenceLabel}: <code className="font-mono select-all">{entry.ref}</code> ·{" "}
          {t.errorCodeLabel}: <code>{entry.code}</code> · {formatDate(entry.at)}
        </p>
      </div>
    );
  };

  // Stays true from the click until the browser has left for the IdP, so the
  // buttons don't flip back between the API response and the navigation
  const [redirectingToIdp, setRedirectingToIdp] = useState<SsoCallbackTestTarget | null>(null);

  /** Test sign-in through the current or the pending callback URL; nothing is saved or linked. */
  const handleTestCallback = async (target: SsoCallbackTestTarget) => {
    const showError = target === "pending" ? setConfirmError : setError;
    showError(null);
    setRedirectingToIdp(target);
    try {
      const { authorizationUrl } = await callbackTestMutation.mutateAsync(target);
      window.location.href = authorizationUrl;
    } catch (err) {
      setRedirectingToIdp(null);
      showError(err instanceof Error ? err.message : t.testStartFailed);
    }
  };

  // From the URL, so any string: only reasons the API can produce are shown (the codes
  // the IdP may send are interpolated); anything else gets the generic "unknown" text.
  const failureReason = callbackTest?.reason ?? "";
  const callbackTestFailure = (() => {
    switch (failureReason) {
      case "access_denied":
        return t.testFailedCancelled;
      case "expired":
        return t.testFailedExpired;
      case "token_endpoint_not_allowed":
        return t.testFailedEndpointNotAllowed;
      case "token_request_failed":
        return t.testFailedRequest;
      case "idp_error":
        return t.testFailedIdpError;
      case "not_configured":
        return t.testFailedNotConfigured;
      case "wrong_callback":
        return t.testFailedWrongCallback;
      case "no_code":
        return t.testFailedNoCode;
      case "client_secret_undecryptable":
        return t.testFailedSecretUndecryptable;
    }
    const known =
      (SSO_AUTHORIZATION_ERROR_CODES as readonly string[]).includes(failureReason) ||
      (SSO_TOKEN_ERROR_CODES as readonly string[]).includes(failureReason) ||
      /^token_http_\d{3}$/.test(failureReason);
    return known ? t.testFailedGeneric(failureReason) : t.testFailedUnknown;
  })();

  /** The test result, in the card whose button started the test. */
  const renderCallbackTestResult = (target: SsoCallbackTestTarget) => {
    if (!callbackTest || callbackTest.target !== target) return null;
    const succeeded = callbackTest.outcome === "success";
    return (
      <Alert
        variant={succeeded ? "success" : "destructive"}
        className={
          succeeded ? undefined : "border-destructive/40 bg-destructive/10 text-foreground"
        }
      >
        {succeeded ? <CheckCircle2 /> : <AlertCircle />}
        <AlertTitle>
          {!succeeded
            ? t.testFailedTitle
            : target === "pending"
              ? t.testSucceededTitle
              : t.testCurrentSucceededTitle}
        </AlertTitle>
        <AlertDescription className="min-w-0 text-muted-foreground">
          {!succeeded
            ? callbackTestFailure
            : target === "pending"
              ? t.testSucceeded
              : t.testCurrentSucceeded}
          {!succeeded &&
            (testResultQuery.data
              ? renderSignInErrorDetail(testResultQuery.data)
              : // Stash unavailable or expired: the reference is still in the URL and the API log.
                !testResultQuery.isFetching &&
                isSsoSignInErrorRef(callbackTest.ref) && (
                  <p className="mt-2 text-xs">
                    {t.referenceLabel}:{" "}
                    <code className="font-mono select-all">{callbackTest.ref}</code>
                  </p>
                ))}
        </AlertDescription>
      </Alert>
    );
  };

  const handleConfirmChange = async () => {
    setConfirmError(null);
    setSuccess(null);
    try {
      await confirmMutation.mutateAsync();
      toast.success(t.changeConfirmed);
    } catch (err) {
      setConfirmError(err instanceof Error ? err.message : t.confirmFailed);
    }
  };

  // Hint only: the stored secret is kept while the issuer and client ID are unchanged, so
  // tell the admin early to re-enter it once they edit either. The server check decides.
  const secretMustBeReentered =
    hasClientSecret &&
    (normalizeIssuer(formData.oidcIssuer) !== normalizeIssuer(initialOidcIssuer || "") ||
      formData.oidcClientId !== (initialOidcClientId || ""));
  const showReenterHint = secretMustBeReentered && formData.oidcClientSecret.trim() === "";

  // Compared with the saved settings (props), which refetch after a save
  const hasChanges =
    ssoEnabled !== initialSsoEnabled ||
    formData.oidcIssuer !== (initialOidcIssuer || "") ||
    formData.oidcClientId !== (initialOidcClientId || "") ||
    formData.oidcName !== (initialOidcName || "") ||
    formData.oidcClientSecret.trim() !== "";

  const confirmBy = pendingChange?.confirmBy ?? null;
  const pendingOverdue = isPastDbTimestamp(confirmBy);
  const pendingBody = !confirmBy
    ? t.pendingBodyNoDate
    : (pendingOverdue ? t.pendingBodyOverdue : t.pendingBody)(formatDate(confirmBy));

  // Secret-only: the server leaves the issuer, client ID and name as stored. Edits to those
  // fields stay in the form; a typed secret is cleared.
  const handleRemoveSecret = async () => {
    setError(null);
    setSuccess(null);
    try {
      await updateMutation.mutateAsync({ removeClientSecret: true });
      setSuccess(t.secretRemoved);
      setFormData((prev) => ({ ...prev, oidcClientSecret: "" }));
    } catch (err) {
      setError(err instanceof Error ? err.message : t.failedToSave);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setTestResult(null);

    // Only a confirmation shown for this save applies, and never when SSO is switched off
    const confirmedIssuer =
      ssoEnabled && issuerDiff && issuerConfirmed ? issuerDiff.discovered : undefined;

    try {
      if (ssoEnabled) {
        const saved = await updateMutation.mutateAsync({
          oidcIssuer: formData.oidcIssuer,
          confirmedIssuer,
          oidcClientId: formData.oidcClientId,
          oidcClientSecret: formData.oidcClientSecret.trim() || undefined,
          oidcName: formData.oidcName || undefined,
        });
        // The field shows what was stored, which may differ from what was typed
        setFormData((prev) => ({ ...prev, oidcIssuer: saved.issuer }));
      } else {
        await deleteMutation.mutateAsync();
      }
      setIssuerDiff(null);
      setIssuerConfirmed(false);
      setSuccess(confirmedIssuer ? t.savedWithIssuer(confirmedIssuer) : t.savedSuccessfully);
      setFormData((prev) => ({ ...prev, oidcClientSecret: "" }));
    } catch (err) {
      if (err instanceof ApiError && apiErrorCode(err) === "issuer_confirmation_required") {
        const body = err.body as SsoIssuerConfirmationRequiredResponse;
        setIssuerDiff({ entered: body.enteredIssuer, discovered: body.discoveredIssuer });
        setIssuerConfirmed(false);
        setError(t.issuerDiffersNeedsConfirm);
        return;
      }
      setError(err instanceof Error ? err.message : t.failedToSave);
    }
  };

  const handleTestConnection = async () => {
    setError(null);
    setSuccess(null);
    setTestResult(null);
    setIssuerDiff(null);
    setIssuerConfirmed(false);

    if (!formData.oidcIssuer) {
      setError(t.enterIssuerFirst);
      return;
    }

    try {
      const result = await testMutation.mutateAsync(formData.oidcIssuer);
      setTestResult(result);
      if (result.issuerDiffers) {
        setIssuerDiff({ entered: result.enteredIssuer, discovered: result.discoveredIssuer });
      }
      // The warning carries the message when the issuers differ; success styling only when they match
      if (!result.issuerDiffers) setSuccess(t.testSuccessMessage);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.connectionTestFailed);
    }
  };

  return (
    <div className="space-y-6">
      {pendingChange && (
        <Card className="border-warning/40">
          <CardHeader>
            <CardTitle>{t.pendingTitle}</CardTitle>
            <CardDescription>{pendingBody}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ol className="list-decimal pl-5 space-y-3 text-sm">
              <li>
                {t.pendingStep1}
                <code className="text-xs bg-muted p-2 rounded block mt-2 break-all">
                  {pendingChange.newCallbackUrl}
                </code>
              </li>
              <li>{t.pendingStep2}</li>
            </ol>
            {renderCallbackTestResult("pending")}
            {confirmError && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {confirmError}
              </div>
            )}
            <p className="text-xs text-muted-foreground">{t.testCallbackHint}</p>
            {/* The next step is the prominent one: test first, confirm once a test succeeded */}
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant={
                  callbackTest?.target === "pending" && callbackTest.outcome === "success"
                    ? "secondary"
                    : "default"
                }
                onClick={() => handleTestCallback("pending")}
                disabled={isPending || redirectingToIdp !== null}
                loading={redirectingToIdp === "pending"}
              >
                <FlaskConical />
                {t.testCallback}
              </Button>
              <Button
                variant={
                  callbackTest?.target === "pending" && callbackTest.outcome === "success"
                    ? "default"
                    : "secondary"
                }
                onClick={handleConfirmChange}
                disabled={isPending || redirectingToIdp !== null}
                loading={confirmMutation.isPending}
              >
                {t.confirmChange}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card>
        <CardHeader>
          <CardTitle>{t.aboutSso}</CardTitle>
          <CardDescription>{t.aboutSsoDescription}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">{t.aboutSsoDetails}</p>
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-sm font-medium">{t.callbackUrlLabel}</p>
            <code className="text-xs bg-background p-2 rounded block mt-2 break-all">
              {callbackUrl}
            </code>
            {pendingChange && (
              <p className="text-xs text-muted-foreground mt-2">{t.inUseUntilConfirmed}</p>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              {t.providerIdLabel}{" "}
              <code className="bg-background px-1 py-0.5 rounded">{providerId}</code>
            </p>
            {!isConfigured && (
              <p className="text-xs text-muted-foreground mt-2">{t.providerIdNote}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Configuration Card */}
      <Card>
        <CardHeader>
          <CardTitle>{t.ssoConfiguration}</CardTitle>
          <CardDescription>{t.configureOidc}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-6">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}
            {success && (
              <div className="text-sm rounded-md border border-success/40 bg-success/10 p-3">
                {success}
              </div>
            )}

            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <Label htmlFor="ssoEnabled" className="text-base font-medium">
                  {t.enableSso}
                </Label>
                <p className="text-sm text-muted-foreground">{t.enableSsoDescription}</p>
              </div>
              <Switch
                id="ssoEnabled"
                checked={ssoEnabled}
                onCheckedChange={setSsoEnabled}
                disabled={isPending}
              />
            </div>

            {/* OIDC Configuration Fields */}
            {ssoEnabled && (
              <div className="space-y-4 p-4 border rounded-lg">
                <div className="space-y-2">
                  <Label htmlFor="oidcName">{t.providerName}</Label>
                  <Input
                    id="oidcName"
                    value={formData.oidcName}
                    onChange={(e) => setFormData({ ...formData, oidcName: e.target.value })}
                    placeholder={t.providerNamePlaceholder}
                    disabled={isPending}
                  />
                  <p className="text-xs text-muted-foreground">{t.providerNameHint}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="oidcIssuer">
                    {t.issuerUrl} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="oidcIssuer"
                    value={formData.oidcIssuer}
                    onChange={(e) => {
                      setFormData({ ...formData, oidcIssuer: e.target.value });
                      // A confirmation belongs to the issuer it was shown for
                      setIssuerDiff(null);
                      setIssuerConfirmed(false);
                      setTestResult(null);
                      setError(null);
                    }}
                    placeholder={t.issuerUrlPlaceholder}
                    disabled={isPending}
                  />
                  <p className="text-xs text-muted-foreground">{t.issuerUrlHint}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="oidcClientId">
                    {t.clientId} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="oidcClientId"
                    value={formData.oidcClientId}
                    onChange={(e) => setFormData({ ...formData, oidcClientId: e.target.value })}
                    placeholder={t.clientIdPlaceholder}
                    disabled={isPending}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="oidcClientSecret">{t.clientSecret}</Label>
                  <Input
                    id="oidcClientSecret"
                    type="password"
                    value={formData.oidcClientSecret}
                    onChange={(e) => setFormData({ ...formData, oidcClientSecret: e.target.value })}
                    placeholder={
                      hasClientSecret && !showReenterHint
                        ? "••••••••••••"
                        : t.clientSecretPlaceholder
                    }
                    disabled={isPending}
                  />
                  <p className="text-xs text-muted-foreground">
                    {!hasClientSecret
                      ? t.clientSecretRequired
                      : showReenterHint
                        ? t.clientSecretReenter
                        : t.clientSecretExisting}
                  </p>
                  {hasClientSecret && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setRemoveSecretOpen(true)}
                      disabled={isPending}
                    >
                      {t.removeClientSecret}
                    </Button>
                  )}
                  <ConfirmDialog
                    open={removeSecretOpen}
                    onOpenChange={setRemoveSecretOpen}
                    title={t.removeClientSecretTitle}
                    description={t.removeClientSecretDescription}
                    confirmText={t.removeClientSecretConfirm}
                    cancelText={t.cancel}
                    variant="destructive"
                    onConfirm={handleRemoveSecret}
                  />
                </div>

                {/* Test Connection (discovery) and Test sign-in (full round trip, saved settings) */}
                <div className="pt-4 border-t space-y-3">
                  {renderCallbackTestResult("current")}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleTestConnection}
                      disabled={isPending || !formData.oidcIssuer}
                      loading={testMutation.isPending}
                    >
                      {t.testConnection}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => handleTestCallback("current")}
                      disabled={
                        isPending || redirectingToIdp !== null || !isConfigured || hasChanges
                      }
                      loading={redirectingToIdp === "current"}
                    >
                      <FlaskConical />
                      {t.testSignIn}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {isConfigured && hasChanges ? t.saveBeforeTest : t.testSignInHint}
                  </p>

                  {isConfigured && (
                    <div className="space-y-2 pt-2">
                      <Label htmlFor="sso-sign-in-error-lookup">{t.lookupTitle}</Label>
                      <p className="text-xs text-muted-foreground">{t.lookupHint}</p>
                      <div className="flex flex-wrap gap-2">
                        <Input
                          id="sso-sign-in-error-lookup"
                          value={lookupInput}
                          onChange={(e) => {
                            setLookupInput(e.target.value);
                            setLookupInvalid(false);
                            setLookupRef(null);
                          }}
                          onKeyDown={(e) => {
                            // Inside the settings <form>: Enter must look up, not save the form.
                            if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                              e.preventDefault();
                              handleLookup();
                            }
                          }}
                          placeholder={t.lookupPlaceholder}
                          className="max-w-sm"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleLookup}
                          disabled={lookupQuery.isFetching}
                          loading={lookupQuery.isFetching}
                        >
                          {t.lookupButton}
                        </Button>
                      </div>
                      {lookupInvalid && (
                        <p className="text-xs text-destructive">{t.lookupInvalidRef}</p>
                      )}
                      {lookupRef && lookupQuery.isError && (
                        <p className="text-xs text-destructive">{t.lookupFailed}</p>
                      )}
                      {lookupRef && !lookupQuery.isError && lookupQuery.data === null && (
                        <p className="text-xs text-muted-foreground">{t.lookupNotFound}</p>
                      )}
                      {lookupRef && renderSignInErrorDetail(lookupQuery.data)}
                    </div>
                  )}

                  {issuerDiff && (
                    <IssuerDiffersAlert
                      title={t.issuerDiffersTitle}
                      body={t.issuerDiffersBody(issuerDiff.entered, issuerDiff.discovered)}
                      confirmLabel={t.issuerDiffersConfirm(issuerDiff.discovered)}
                      confirmed={issuerConfirmed}
                      onConfirmedChange={(confirmed) => {
                        setIssuerConfirmed(confirmed);
                        // The "confirm before saving" error is answered by ticking the box
                        if (confirmed) setError(null);
                      }}
                      disabled={isPending}
                    />
                  )}

                  {testResult && (
                    <div className="mt-4 p-4 bg-muted rounded-lg text-sm">
                      <p
                        className={
                          testResult.issuerDiffers
                            ? "font-medium text-warning"
                            : "font-medium text-success"
                        }
                      >
                        {testResult.issuerDiffers
                          ? t.connectionFoundDiffers
                          : t.connectionSuccessful}
                      </p>
                      <div className="mt-2 space-y-1 text-muted-foreground">
                        {/* When the issuers differ the warning above already shows both values */}
                        {!testResult.issuerDiffers && (
                          <p>
                            <span className="font-medium">{t.issuer}</span>{" "}
                            {testResult.discoveredIssuer}
                          </p>
                        )}
                        <p>
                          <span className="font-medium">{t.authorization}</span>{" "}
                          {testResult.endpoints?.authorization}
                        </p>
                        <p>
                          <span className="font-medium">{t.token}</span>{" "}
                          {testResult.endpoints?.token}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={isPending || !hasChanges}
                loading={updateMutation.isPending || deleteMutation.isPending}
              >
                {t.save}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
